import { Controller } from "@hotwired/stimulus";

// Reusable bottom sheet, adapted from the waas BottomSheetController.
//
// A trigger opens it with `click->bottom-sheet#open`; it closes via the close
// button or backdrop (`click->bottom-sheet#close`), the Escape key, or a
// downward drag on touch devices. Open animates the sheet up and fades the
// backdrop in; close hides the container immediately (adds `hidden` + `inert`)
// so it never depends on a transitionend event that may not fire.
export default class BottomSheetController extends Controller {
  static targets = ["container", "backdrop", "sheet"];
  static values = { closeDeltaY: { type: Number, default: 100 } };

  connect() {
    this._onKeydown = (e) => {
      if (e.key === "Escape") this.close();
    };
    this._onTouchStart = this.handleTouchStart.bind(this);
    this._onTouchMove = this.handleTouchMove.bind(this);
    this._onTouchEnd = this.handleTouchEnd.bind(this);
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown);
    this.teardownDrag();
  }

  open(event) {
    if (event) event.preventDefault();
    if (!this.hasContainerTarget) return;

    const container = this.containerTarget;
    container.classList.remove("hidden");
    container.removeAttribute("inert");
    container.removeAttribute("aria-hidden");

    // The theme surface tokens are translucent (glass over the page background),
    // so a modal sheet would let page content show through. Force the panel fully
    // opaque by resolving its themed background color and dropping the alpha —
    // keeps the exact theme hue in light and dark, just solid.
    this.forceOpaqueSurface();

    // Animate in from a lowered, transparent state.
    if (this.hasBackdropTarget) this.backdropTarget.style.opacity = "0";
    if (this.hasSheetTarget) {
      this.sheetTarget.style.transform = "translateY(16px)";
      this.sheetTarget.style.opacity = "0";
    }
    requestAnimationFrame(() => {
      if (this.hasBackdropTarget) this.backdropTarget.style.opacity = "1";
      if (this.hasSheetTarget) {
        this.sheetTarget.style.transform = "translateY(0)";
        this.sheetTarget.style.opacity = "1";
      }
    });

    document.addEventListener("keydown", this._onKeydown);
    if (this.isTouchDevice()) this.setupDrag();
    if (this.hasSheetTarget && this.sheetTarget.focus) this.sheetTarget.focus();
  }

  close() {
    if (!this.hasContainerTarget || this.containerTarget.classList.contains("hidden")) return;

    const container = this.containerTarget;
    container.classList.add("hidden");
    container.setAttribute("inert", "");
    container.setAttribute("aria-hidden", "true");

    document.removeEventListener("keydown", this._onKeydown);
    this.teardownDrag();

    // Reset inline styles so the next open animates (and re-resolves) cleanly.
    if (this.hasSheetTarget) {
      this.sheetTarget.style.transform = "";
      this.sheetTarget.style.opacity = "";
      this.sheetTarget.style.backgroundColor = "";
    }
    if (this.hasBackdropTarget) this.backdropTarget.style.opacity = "";
  }

  // Resolve the panel's themed background color and re-apply it with the alpha
  // stripped, so a translucent theme surface token becomes a solid, opaque panel
  // (same hue) that never lets page content show through.
  forceOpaqueSurface() {
    if (!this.hasSheetTarget) return;
    const resolved = getComputedStyle(this.sheetTarget).backgroundColor;
    const parts = resolved?.match(/[\d.]+/g);
    if (parts && parts.length >= 3) {
      this.sheetTarget.style.backgroundColor = `rgb(${parts[0]}, ${parts[1]}, ${parts[2]})`;
    }
  }

  isTouchDevice() {
    return window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window;
  }

  setupDrag() {
    if (!this.hasSheetTarget) return;
    this._dragStartY = 0;
    this._dragging = false;
    this.sheetTarget.addEventListener("touchstart", this._onTouchStart, { passive: true });
    this.sheetTarget.addEventListener("touchmove", this._onTouchMove, { passive: true });
    this.sheetTarget.addEventListener("touchend", this._onTouchEnd, { passive: true });
  }

  teardownDrag() {
    if (!this.hasSheetTarget) return;
    this.sheetTarget.removeEventListener("touchstart", this._onTouchStart);
    this.sheetTarget.removeEventListener("touchmove", this._onTouchMove);
    this.sheetTarget.removeEventListener("touchend", this._onTouchEnd);
  }

  handleTouchStart(event) {
    this._dragStartY = event.touches[0].clientY;
    this._dragging = false;
  }

  handleTouchMove(event) {
    if (!this._dragStartY) return;
    const deltaY = event.touches[0].clientY - this._dragStartY;
    if (deltaY > 40) this._dragging = true;
    if (deltaY > 0) {
      this.sheetTarget.style.transform = `translateY(${Math.min(deltaY, 400)}px)`;
    }
  }

  handleTouchEnd(event) {
    if (!this._dragStartY) return;
    const deltaY = event.changedTouches[0].clientY - this._dragStartY;
    if (this._dragging && deltaY > this.closeDeltaYValue) {
      this.close();
    } else if (this.hasSheetTarget) {
      this.sheetTarget.style.transform = "translateY(0)";
    }
    this._dragStartY = 0;
    this._dragging = false;
  }
}
